import React, {useState} from "react"
import {Box, CardContent, Chip} from "@mui/material";
import CCOHeadline from "../../fundamentals/CCOHeadline";
import CCORichText from "../../fundamentals/CCORichText/CCORichText";
import CCORoofline from "../../fundamentals/CCORoofline";
import {textDecorationStyle} from "../../fundamentals/CCOLinkText";
import CCODownloadButton from "../../fundamentals/CCODownloadButton";
import {useTheme} from "@mui/material/styles";
import {isTargetCMDownload} from "./TeaserCard";
import TeaserCardCallToAction, {CallToActionType} from "./TeaserCardCallToAction";
import CCOPicture from "../../fundamentals/CCOPicture";
import useWindowSize from "../../../utility/useWindowSize";

interface Props {
    item: any
    pill?: string
    horizontal?: boolean
    headlineType?: string
    descriptionCharLimit?: number
    callToActionType?: CallToActionType
    callToActionCustomText?: string
    showHorizontalPictureOnRight?: boolean,
    hero?: boolean
}

export const DESCRIPTION_CHAR_LIMIT = 151;
export const HEADER_CHAR_LIMIT = 58;
export const ROOF_LINE_CHAR_LIMIT = 27;

export const cardStyle = (props: Props, color: string) => (
    {
        display: 'grid',
        gridTemplateColumns: props.horizontal ? '1fr 1fr' : 'unset',
        gridTemplateRows: props.horizontal ? 'unset' : 'max-content max-content',
        height: '100%!important',
        width: '100%!important',
        '&:hover': {
            '& h3, h5': {
                color: !props.item.teaserTarget ? 'inherit' : color,
                transition: !props.item.teaserTarget ? 'unset' : 'color 1s ease-out',
            },
            '& div > p': !props.item.teaserTarget ? 'unset' : textDecorationStyle,
        },
    });

const pillCardStyle = (props: Props, color: string) => (
    {
        ...cardStyle(props, color), ...{background: 'var(--background-grey)'}
    });

function TeaserCardContent(props: Props) {
    const theme = useTheme();
    const color = theme.palette.primary.main;
    const [isCardHovered, setIsCardHovered] = useState(false);
    const handleMouseOver = () => setIsCardHovered(true);
    const handleMouseOut = () => setIsCardHovered(false);
    return <Box id={props.item.id}
                sx={props.pill ? pillCardStyle(props, color) : cardStyle(props, color)}
                onMouseOver={handleMouseOver}
                onMouseOut={handleMouseOut}>
        {renderContent(props, color, isCardHovered)}
    </Box>
}

function renderContent(props: Props, color: string, isCardHovered: boolean) {
    return props.horizontal && props.showHorizontalPictureOnRight ?
        <React.Fragment>
            {renderText(props, color, isCardHovered)}
            {renderPicture(props)}
        </React.Fragment> :
        <React.Fragment>
            {renderPicture(props)}
            {renderText(props, color, isCardHovered)}
        </React.Fragment>
}

function renderPicture(props: Props) {
    return props.item.picture &&
        <Box width={"585"}
             height={"329"} overflow={'hidden'}
             sx={{"img": {width: '100%!important', height: '100%!important'}}}>
            <CCOPicture name={props.item.picture?.name}
                        picture={props.item.picture}
                        orientation={"landscape_ratio16x9"} width={"585"}
                        height={"329"}/>
        </Box>;
}

function renderText(props: Props, color: string, isCardHovered: boolean) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    let {greaterThanWindowMedium, lowerThanWindowMedium, lowerThanWindowSmall} = useWindowSize();

    const CARD_CONTENT_STYLE = {
        'div:last-child': {
            marginBottom: '0 !important',
        },
        'span > div:last-child': {
            marginBottom: '24px !important',
        },
        paddingLeft: (props.hero && (greaterThanWindowMedium || lowerThanWindowSmall)) || lowerThanWindowMedium ? '16px' : '24px',
        paddingTop: (props.hero && (greaterThanWindowMedium || lowerThanWindowSmall)) || lowerThanWindowMedium ? '16px' : '24px',
        height: '100%!important',
        width: '100%!important',
    }
    const PILL_CONTENT_STYLE = {...CARD_CONTENT_STYLE, ...{gridTemplateRows: 'max-content 2fr max-content'}}


    return <CardContent tabIndex={props.callToActionType ? 0 : undefined}
                        sx={props.pill ? PILL_CONTENT_STYLE : CARD_CONTENT_STYLE}>
        {renderPillBox(props, color)}
        {renderTitle(props)}
        {renderRichText(props)}
        {renderDownloadButton(props, isCardHovered)}
        <TeaserCardCallToAction item={props.item}
                                callToActionType={props.callToActionType}
                                callToActionCustomText={props.callToActionCustomText}
        />
    </CardContent>
}

function renderPillBox(props: Props, color: string) {
    return props.pill &&
        <Box style={{marginBottom: '10px'}}>
            <Chip label={props.pill} style={{
                backgroundColor: color,
                color: 'white'
            }} size='small' variant='outlined'/>
        </Box>
}

function renderTitle(props: Props) {
    return <Box>
        <CCORoofline metadataId={props.item.id} roofLine={props.item.headline} roofLineCharLimit={ROOF_LINE_CHAR_LIMIT}
                     propertyName='properties.headline'/>
        <CCOHeadline type={props.headlineType ? props.headlineType : 'h5'} propertyName={'properties.teaserTitle'}
                     metadataId={props.item.id} headline={props.item.teaserTitle}
                     limit={HEADER_CHAR_LIMIT} justify='left'/>
    </Box>;
}

function renderRichText(props: Props) {
    const descriptionCharLimit = props.descriptionCharLimit ? props.descriptionCharLimit : DESCRIPTION_CHAR_LIMIT
    return <CCORichText metadataId={props.item.id} textAsTree={props.item.teaserText?.textAsTree} propertyName='properties.teaserText'
                        charLimit={descriptionCharLimit}/>;
}

function renderDownloadButton(props: Props, isCardHovered: boolean) {
    return isTargetCMDownload(props.item) &&
        <Box sx={{display: 'flex', justifyContent: 'left'}}>
            <CCODownloadButton download={[props.item.teaserTarget]}
                               downloadTitle={props.item.teaserTarget.teaserTitle}
                               noLink={true}
                               isHovered={isCardHovered}/>
        </Box>;
}

export default TeaserCardContent


